import { SearchInput, spacing, Text } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useChat } from '../../hooks/useChat';
import { useChatHistory } from '../../hooks/useChatHistory';
import { GuardianContext } from '../GuardianProvider';
import { ChatHistoryItem } from './ChatHistoryItem';
import { SidebarFooter } from './SidebarFooter';

const SearchInputBox = styled.div`
  margin: ${spacing.r8};
  align-self: center;
`;

/** Conversation-mode sidebar body: searchable chat history + clear-history footer. */
export const ConversationList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value), []);
  const handleSearchReset = useCallback(() => setSearchTerm(''), []);

  const { conversationId } = useContext(GuardianContext);
  const { conversation } = useChat();
  const isUserOnNewChat = conversation.isNew;

  const { chatHistory } = useChatHistory();
  const chatHistoryFiltered = useMemo(
    () =>
      chatHistory.filter(({ title }) =>
        String(title ?? '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    [chatHistory, searchTerm],
  );

  return (
    <>
      <SearchInputBox>
        <SearchInput
          id="search-chat"
          placeholder="Search..."
          onChange={handleSearchChange}
          value={searchTerm}
          size="2/3"
          onReset={handleSearchReset}
        />
      </SearchInputBox>
      <Box flexDirection="column" flexGrow={1} overflowY="auto" role="list">
        {isUserOnNewChat && (
          <ChatHistoryItem key={conversationId} id={conversationId} title="Untitled conversation" variant="italic" />
        )}
        {chatHistoryFiltered.map(({ conversationId: id, title }) => (
          <ChatHistoryItem key={id} id={id} title={title} />
        ))}
        {searchTerm && chatHistoryFiltered.length === 0 && (
          <Box display="flex" padding={spacing.r16} justifyContent="center">
            <Text color="textSecondary">No results found</Text>
          </Box>
        )}
      </Box>
      <SidebarFooter />
    </>
  );
};
